import React from 'react';
import { useParams, Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import SectionTitle from '../components/SectionTitle';
import { ServerIcon, CheckIcon } from 'lucide-react';
const SolutionDetail = () => {
  const {
    category,
    id
  } = useParams<{
    category: string;
    id: string;
  }>();
  const solutions = {
    'all-in-one': {
      title: '一体机解决方案',
      icon: <ServerIcon className="h-12 w-12" data-id="element-579" />,
      solutions: {
        office: {
          title: '智能办公一体机解决方案',
          subtitle: '集成文档处理、会议助手、智能翻译等功能，全面提升办公效率和体验',
          description: `我们的智能办公一体机解决方案是专为现代企业办公环境设计的智能化产品，集成了多种AI功能，全面提升办公效率和体验。
该解决方案将强大的硬件性能与先进的AI软件系统完美结合，提供文档智能处理、会议实时记录与总结、多语言实时翻译等功能，为企业打造高效、智能的办公环境。`,
          features: [{
            title: '智能文档处理',
            description: '自动识别、分类和提取文档信息，支持多种格式文档的智能分析和处理。'
          }, {
            title: '会议助手',
            description: '实时记录会议内容，自动生成会议纪要和行动项，提高会议效率。'
          }, {
            title: '智能翻译',
            description: '支持多语言实时翻译，满足国际化办公需求，促进跨语言沟通。'
          }, {
            title: '智能搜索与知识管理',
            description: '快速检索企业文档和知识库，提供精准的信息获取渠道。'
          }, {
            title: '办公自动化',
            description: '自动化处理日常办公任务，如邮件分类、日程安排、报表生成等。'
          }, {
            title: '安全可控',
            description: '企业级安全防护，数据本地处理，确保信息安全和隐私保护。'
          }],
          benefits: ['提高工作效率：自动化处理重复性工作，员工可专注于创造性任务', '降低沟通成本：实时翻译和会议助手功能，提升沟通效率', '知识沉淀：智能归档和知识管理，促进企业知识积累和共享', '决策支持：智能分析和报表生成，辅助管理决策', '降低IT成本：一体化设计，减少多系统维护成本'],
          specifications: [{
            name: '处理器',
            value: 'Intel Core i7/i9 或 AMD Ryzen 7/9'
          }, {
            name: '内存',
            value: '32GB/64GB DDR4'
          }, {
            name: '存储',
            value: '1TB/2TB NVMe SSD'
          }, {
            name: '显示屏',
            value: '27英寸 4K触控屏'
          }, {
            name: 'AI加速',
            value: '内置NPU或专用AI处理单元'
          }, {
            name: '操作系统',
            value: 'Windows/Linux，预装AI办公套件'
          }],
          image: 'https://images.unsplash.com/photo-1563770660941-10a63607692e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
        },
        deepseek: {
          title: 'Deepseek一体机解决方案',
          subtitle: '基于Deepseek大模型的专用一体机，提供强大的AI能力和优化的用户体验',
          description: `Deepseek一体机解决方案是我们与Deepseek合作开发的专业AI一体机产品，集成了Deepseek先进的大语言模型能力，为企业提供本地部署的高性能AI解决方案。
该一体机预装优化的Deepseek模型，支持自然语言理解、代码生成、知识问答等多种功能，同时保证数据安全性和隐私保护。通过简单的配置，企业可以快速获得强大的AI能力，无需复杂的技术部署和维护。`,
          features: [{
            title: 'Deepseek大模型本地部署',
            description: '预装优化的Deepseek模型，支持离线运行，保障数据安全。'
          }, {
            title: '多场景AI能力',
            description: '支持文本生成、代码编写、知识问答、文档分析等多种AI功能。'
          }, {
            title: '定制化训练接口',
            description: '提供模型微调接口，支持基于企业数据的定制化训练。'
          }, {
            title: '高性能计算',
            description: '配备高性能GPU，优化的推理引擎，提供低延迟的AI服务体验。'
          }, {
            title: '易用的管理界面',
            description: '直观的Web管理界面，简化系统配置和维护，支持多用户管理。'
          }, {
            title: 'API服务',
            description: '提供标准化API接口，方便与企业现有系统集成。'
          }],
          benefits: ['数据安全：本地部署，数据不出企业内网，满足合规要求', '高性能：专为Deepseek模型优化的硬件配置，提供最佳性能体验', '即插即用：预装配置，开箱即用，降低技术门槛', '成本可控：一次性投入，无需持续的API调用费用', '灵活扩展：支持多种业务场景扩展，满足企业多样化需求'],
          specifications: [{
            name: 'GPU',
            value: 'NVIDIA A10/A100/H100'
          }, {
            name: 'CPU',
            value: 'Intel Xeon或AMD EPYC系列'
          }, {
            name: '内存',
            value: '128GB/256GB DDR4 ECC'
          }, {
            name: '存储',
            value: '4TB/8TB NVMe SSD RAID'
          }, {
            name: '网络',
            value: '10Gbps以太网，支持冗余配置'
          }, {
            name: '预装模型',
            value: 'Deepseek-7B/33B，Code-33B等'
          }],
          image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2034&q=80'
        },
        code: {
          title: '代码生成一体机解决方案',
          subtitle: '专为软件开发团队设计，提供代码生成、代码审查和优化建议等功能',
          description: `代码生成一体机解决方案是专为软件开发团队设计的AI辅助开发工具，集成了先进的代码生成模型和开发辅助功能，帮助开发者提高编码效率和代码质量。
该解决方案预装了优化的代码生成大模型，支持多种编程语言和框架，能够根据自然语言描述或注释生成高质量代码，同时提供代码审查、Bug修复建议和性能优化分析等功能。`,
          features: [{
            title: '智能代码生成',
            description: '基于自然语言描述自动生成符合规范的代码，支持多种编程语言和框架。'
          }, {
            title: '代码补全与优化',
            description: '实时代码补全建议，自动优化代码结构和性能。'
          }, {
            title: '代码审查与Bug检测',
            description: '自动识别潜在Bug和安全漏洞，提供修复建议。'
          }, {
            title: 'API文档生成',
            description: '自动为代码生成规范的文档和注释，提高代码可维护性。'
          }, {
            title: 'IDE集成',
            description: '与主流IDE无缝集成，提供一致的开发体验。'
          }, {
            title: '团队协作功能',
            description: '支持多用户协作，代码知识共享和版本管理。'
          }],
          benefits: ['提高开发效率：自动化代码生成和补全，减少重复性编码工作', '提升代码质量：自动代码审查和优化建议，减少Bug和安全漏洞', '降低开发门槛：辅助初级开发者快速上手复杂项目', '知识沉淀：积累团队编码规范和最佳实践', '本地部署：确保代码安全，满足企业对知识产权保护的需求'],
          specifications: [{
            name: 'GPU',
            value: 'NVIDIA RTX A5000/A6000或同等性能GPU'
          }, {
            name: 'CPU',
            value: 'Intel Core i9或AMD Ryzen 9系列'
          }, {
            name: '内存',
            value: '64GB/128GB DDR4'
          }, {
            name: '存储',
            value: '2TB/4TB NVMe SSD'
          }, {
            name: '支持语言',
            value: 'Python, Java, C++, JavaScript, Go等20+种语言'
          }, {
            name: 'IDE插件',
            value: 'VSCode, JetBrains系列, Eclipse等'
          }],
          image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
        }
      }
    },
    embodied: {
      title: '具身智能解决方案',
      icon: <div className="h-12 w-12" data-id="element-580" />,
      solutions: {
        simulation: {
          title: '具深智能仿真平台',
          subtitle: '提供高精度的物理仿真环境，加速机器人和智能设备的训练和测试过程',
          description: `具深智能仿真平台是一款专业的机器人和智能设备仿真系统，提供高精度的物理仿真环境和丰富的场景模拟能力，用于加速智能体的训练、测试和验证过程。
该平台集成了先进的物理引擎和渲染技术，可以精确模拟现实世界的物理特性和环境交互，为机器人算法开发提供可靠的虚拟测试环境。通过仿真平台，开发者可以在虚拟环境中快速迭代和优化算法，大幅降低实物测试的成本和风险。`,
          features: [{
            title: '高精度物理引擎',
            description: '精确模拟刚体动力学、柔性物体、流体等物理特性，保证仿真结果的真实性。'
          }, {
            title: '多样化场景库',
            description: '内置丰富的环境模型和场景库，支持自定义场景创建和编辑。'
          }, {
            title: '传感器仿真',
            description: '模拟各类传感器数据，包括视觉、激光雷达、IMU等，支持传感器噪声模拟。'
          }, {
            title: '分布式训练支持',
            description: '支持并行仿真和分布式训练，加速强化学习和其他AI算法的训练过程。'
          }, {
            title: '数字孪生集成',
            description: '支持与实际设备的数字孪生集成，实现虚实结合的开发和测试流程。'
          }, {
            title: 'API接口与开发工具',
            description: '提供丰富的API接口和开发工具，支持主流AI框架和机器人开发环境。'
          }],
          benefits: ['降低开发成本：虚拟环境测试，减少硬件原型和实验成本', '加速开发周期：快速迭代算法，缩短开发周期', '提高安全性：在虚拟环境中测试高风险场景，避免实物测试的安全隐患', '扩展测试覆盖：模拟各种极端场景和边缘情况，提高算法鲁棒性', '支持协作开发：多用户协作环境，促进团队协作和知识共享'],
          specifications: [{
            name: '支持平台',
            value: 'Windows, Linux, 云端部署'
          }, {
            name: '物理引擎',
            value: '自研高精度物理引擎，支持GPU加速'
          }, {
            name: '渲染引擎',
            value: '基于RTX的实时光线追踪渲染'
          }, {
            name: '支持机器人类型',
            value: '移动机器人、机械臂、四足机器人、无人机等'
          }, {
            name: 'AI框架集成',
            value: 'PyTorch, TensorFlow, RLlib等'
          }, {
            name: 'ROS集成',
            value: '完全支持ROS/ROS2生态'
          }],
          image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2006&q=80'
        },
        humanoid: {
          title: '人形机器人解决方案',
          subtitle: '为人形机器人提供先进的控制系统和智能决策能力，实现复杂任务执行',
          description: `人形机器人解决方案是我们提供的全栈式人形机器人技术方案，涵盖硬件参考设计、控制系统、感知系统和决策系统等核心模块，帮助客户快速开发和部署人形机器人应用。
该解决方案集成了先进的运动控制算法和大模型驱动的决策系统，赋予人形机器人自然、流畅的动作能力和智能的环境理解与交互能力，适用于服务、教育、娱乐等多种应用场景。`,
          features: [{
            title: '先进运动控制系统',
            description: '基于优化的全身动力学控制算法，实现稳定、自然的步态和动作。'
          }, {
            title: '多模态感知系统',
            description: '集成视觉、听觉、触觉等多种传感器，构建全面的环境感知能力。'
          }, {
            title: '大模型驱动的决策系统',
            description: '基于大模型的多模态理解和决策系统，支持自然语言交互和复杂场景理解。'
          }, {
            title: '模块化设计',
            description: '硬件和软件均采用模块化设计，支持灵活配置和扩展。'
          }, {
            title: '仿真与实物协同开发',
            description: '提供仿真环境和数字孪生工具，支持算法快速迭代和验证。'
          }, {
            title: '开发工具链',
            description: '完整的开发工具链和SDK，简化应用开发和部署流程。'
          }],
          benefits: ['技术门槛降低：提供成熟的技术方案，降低人形机器人开发难度', '开发周期缩短：模块化设计和完善的工具链，加速产品开发周期', '性能保障：经过验证的控制算法和决策系统，确保机器人性能和稳定性', '应用场景广泛：适用于服务、教育、娱乐、科研等多种场景', '持续升级：提供长期的技术支持和升级服务'],
          specifications: [{
            name: '控制系统',
            value: '实时控制系统，1kHz控制频率'
          }, {
            name: '感知系统',
            value: 'RGB-D相机、麦克风阵列、力传感器等'
          }, {
            name: '决策系统',
            value: '基于大模型的多模态理解和决策引擎'
          }, {
            name: '开发接口',
            value: 'C++/Python API，ROS/ROS2支持'
          }, {
            name: '仿真环境',
            value: '集成具深仿真平台，支持控制算法验证'
          }, {
            name: '参考设计',
            value: '提供机械、电子、控制系统参考设计'
          }],
          image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
        }
      }
    },
    chips: {
      title: '国产AI芯片解决方案',
      icon: <div className="h-12 w-12" data-id="element-581" />,
      solutions: {
        'robot-dog': {
          title: '四足机器狗解决方案',
          subtitle: '基于国产AI芯片的四足机器人解决方案，适用于巡检、搜救等复杂环境应用',
          description: `四足机器狗解决方案是我们基于国产AI芯片开发的高性能四足机器人整体解决方案，专为工业巡检、安防监控、灾难救援等复杂环境应用场景设计。
该解决方案集成了国产高性能AI芯片、先进的运动控制系统和环境感知系统，实现了四足机器人在复杂地形的稳定运动和智能决策，具有出色的环境适应能力和任务执行能力。`,
          features: [{
            title: '国产AI芯片加速',
            description: '采用国产高性能AI芯片，提供强大的本地计算能力，支持实时感知和决策。'
          }, {
            title: '先进运动控制算法',
            description: '基于模型预测控制和强化学习的运动控制系统，实现复杂地形的稳定行走。'
          }, {
            title: '多传感器融合感知',
            description: '集成视觉、激光雷达、IMU等多种传感器，构建全面的环境感知能力。'
          }, {
            title: '自主导航与规划',
            description: '支持未知环境自主探索、地图构建和路径规划，适应复杂场景应用。'
          }, {
            title: '任务规划与执行',
            description: '基于大模型的任务理解和规划系统，支持复杂指令执行和任务分解。'
          }, {
            title: '远程监控与控制',
            description: '提供远程监控和控制界面，支持人机协作模式。'
          }],
          benefits: ['国产化替代：基于国产AI芯片，实现关键技术自主可控', '适应复杂环境：优异的地形适应能力，适用于工业、灾难等复杂场景', '低功耗高性能：优化的系统设计，实现长时间续航和高性能计算', '开放扩展：模块化设计，支持功能定制和扩展', '全生命周期支持：提供从开发到部署的全流程技术支持'],
          specifications: [{
            name: 'AI芯片',
            value: '国产高性能AI芯片，8-16TOPS算力'
          }, {
            name: '传感器',
            value: '深度相机、激光雷达、IMU、力传感器等'
          }, {
            name: '负载能力',
            value: '5-15kg，根据型号不同'
          }, {
            name: '续航时间',
            value: '2-4小时连续工作'
          }, {
            name: '防护等级',
            value: 'IP65，适应室内外各种环境'
          }, {
            name: '开发接口',
            value: 'C++/Python SDK，ROS支持'
          }],
          image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
        },
        performance: {
          title: '国产化AI芯片性能测试解决方案',
          subtitle: '全面评估国产AI芯片性能的测试方案，提供详细的性能报告和优化建议',
          description: `国产化AI芯片性能测试解决方案是一套专业的AI芯片评估体系，提供全面、客观的性能测试和分析服务，帮助用户了解国产AI芯片的真实性能表现和应用适配性。
该解决方案包含标准化的测试流程、全面的测试指标体系和专业的分析工具，覆盖计算性能、能耗效率、框架兼容性等多个维度，为芯片选型和应用优化提供可靠依据。`,
          features: [{
            title: '全面的测试指标体系',
            description: '覆盖计算性能、能耗效率、精度、延迟等关键指标，全方位评估芯片性能。'
          }, {
            title: '标准化测试流程',
            description: '规范的测试方法和流程，确保测试结果的可比性和可重复性。'
          }, {
            title: '多场景应用测试',
            description: '包含计算机视觉、自然语言处理、推荐系统等多种实际应用场景测试。'
          }, {
            title: '深入性能分析',
            description: '提供芯片性能瓶颈分析和优化建议，帮助用户充分发挥芯片性能。'
          }, {
            title: '框架兼容性评估',
            description: '评估芯片与主流AI框架的兼容性和优化程度，指导应用开发。'
          }, {
            title: '定制化测试方案',
            description: '根据用户需求定制专属测试方案，针对特定应用场景进行评估。'
          }],
          benefits: ['客观评估：提供公正、客观的芯片性能数据，辅助决策', '降低风险：全面了解芯片性能特点，降低选型风险', '优化指导：针对性的优化建议，充分发挥芯片性能', '成本节约：避免不适合的芯片选择，节约研发和部署成本', '加速应用：快速确定最适合的芯片和优化方案，加速应用落地'],
          specifications: [{
            name: '支持芯片',
            value: '昆仑芯、天数智芯、寒武纪等主流国产AI芯片'
          }, {
            name: '测试模型',
            value: 'ResNet, BERT, YOLOv5等30+种主流模型'
          }, {
            name: '性能指标',
            value: '吞吐量、延迟、能耗、精度等多维度指标'
          }, {
            name: '框架支持',
            value: 'TensorFlow, PyTorch, ONNX, TNN等'
          }, {
            name: '测试工具',
            value: '自研性能分析工具套件'
          }, {
            name: '报告输出',
            value: '详细测试报告和优化建议'
          }],
          image: 'https://images.unsplash.com/photo-1591405351990-4726e331f141?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
        },
        cluster: {
          title: '国产化异构集群解决方案',
          subtitle: '基于多种国产AI芯片构建的高性能计算集群，满足大规模AI训练和推理需求',
          description: `国产化异构集群解决方案是一套基于国产AI芯片构建的高性能计算平台，整合多种国产计算资源，为大规模AI训练和推理提供强大的算力支持。
该解决方案采用异构架构设计，灵活组合CPU、GPU、NPU等多种国产计算芯片，通过优化的资源调度和任务分配机制，实现计算效率最大化。同时，提供完善的集群管理和监控工具，简化运维管理，提升系统可靠性。`,
          features: [{
            title: '多芯片异构架构',
            description: '支持多种国产AI芯片协同工作，优化不同计算任务的资源分配。'
          }, {
            title: '智能调度系统',
            description: '基于任务特性自动选择最优算力资源，提高集群资源利用率。'
          }, {
            title: '高性能网络互连',
            description: '采用高带宽、低延迟的网络架构，提升数据传输效率和节点协同能力。'
          }, {
            title: '分布式存储系统',
            description: '高性能分布式存储，支持海量数据管理和高速数据访问。'
          }, {
            title: '全栈软件优化',
            description: '从底层驱动到上层框架的全栈优化，充分发挥国产芯片性能。'
          }, {
            title: '完善的管理工具',
            description: '提供集群监控、资源管理、任务调度等完整管理工具套件。'
          }],
          benefits: ['国产化替代：基于国产芯片构建，实现关键基础设施自主可控', '性能提升：异构架构设计和优化调度，提供卓越的计算性能', '成本优化：根据任务特性灵活调度资源，优化成本结构', '灵活扩展：模块化设计，支持按需扩展集群规模', '简化管理：完善的管理工具，降低运维复杂度和成本'],
          specifications: [{
            name: '支持芯片',
            value: '国产CPU、GPU、NPU、专用AI加速器'
          }, {
            name: '节点规模',
            value: '支持数十至数千节点的大规模集群'
          }, {
            name: '网络互连',
            value: '100Gbps RDMA，支持InfiniBand或RoCE'
          }, {
            name: '存储系统',
            value: '分布式并行文件系统，支持PB级数据存储'
          }, {
            name: '软件栈',
            value: '优化的AI框架和库，支持主流开发环境'
          }, {
            name: '管理系统',
            value: '集群监控、资源管理、作业调度、故障恢复等'
          }],
          image: 'https://images.unsplash.com/photo-1518770660967-092793a63714?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
        }
      }
    }
  };
  const categoryData = category ? solutions[category as keyof typeof solutions] : null;
  const solution = categoryData && id ? categoryData.solutions[id as keyof typeof categoryData.solutions] : null;
  if (!categoryData || !solution) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-582">
        <div className="text-center" data-id="element-583">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-584">
            解决方案不存在
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-585">
            您查找的解决方案不存在或已被移除。
          </p>
          <Link to="/solutions" className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-md transition duration-300" data-id="element-586">
            返回解决方案列表
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-white" data-id="element-587">
      <HeroSection title={solution.title} subtitle={solution.subtitle} backgroundImage={solution.image} size="medium" data-id="element-588" />
      <section className="py-16 md:py-24" data-id="element-589">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-590">
          <div className="flex flex-col md:flex-row gap-12" data-id="element-591">
            <div className="w-full md:w-2/3" data-id="element-592">
              <div className="mb-12" data-id="element-593">
                <SectionTitle title="解决方案概述" alignment="left" titleSize="medium" data-id="element-594" />
                <div className="prose prose-lg max-w-none" data-id="element-595">
                  {solution.description.split('\n\n').map((paragraph, index) => <p key={index} className="mb-4 text-gray-700" data-id="element-596">
                        {paragraph}
                      </p>)}
                </div>
              </div>
              <div className="mb-12" data-id="element-597">
                <SectionTitle title="核心功能" alignment="left" titleSize="medium" data-id="element-598" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-599">
                  {solution.features.map((feature, index) => <div key={index} className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary-500 transition-colors duration-300" data-id="element-600">
                      <h3 className="text-xl font-semibold mb-3 text-gray-900" data-id="element-601">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-602">{feature.description}</p>
                    </div>)}
                </div>
              </div>
              <div className="mb-12" data-id="element-603">
                <SectionTitle title="核心优势" alignment="left" titleSize="medium" data-id="element-604" />
                <div className="bg-gray-50 p-6 rounded-lg" data-id="element-605">
                  <ul className="space-y-3" data-id="element-606">
                    {solution.benefits.map((benefit, index) => <li key={index} className="flex items-start" data-id="element-607">
                        <CheckIcon className="h-5 w-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-608" />
                        <span className="text-gray-700" data-id="element-609">{benefit}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
            <div className="w-full md:w-1/3" data-id="element-610">
              <div className="bg-gray-50 p-6 rounded-lg sticky top-24" data-id="element-611">
                <div className="text-primary-500 mb-4" data-id="element-612">{categoryData.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-613">
                  技术规格
                </h3>
                <div className="space-y-4" data-id="element-614">
                  {solution.specifications.map((spec, index) => <div key={index} className="border-b border-gray-200 pb-3" data-id="element-615">
                      <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-616">
                        {spec.name}
                      </div>
                      <div className="text-base text-gray-900" data-id="element-617">
                        {spec.value}
                      </div>
                    </div>)}
                </div>
                <div className="mt-8" data-id="element-618">
                  <Link to="/about/contact" className="inline-block w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-md text-center transition duration-300" data-id="element-619">
                    咨询详情
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-620">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-621">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-622">
            需要更多信息？
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto" data-id="element-623">
            我们的专业团队随时为您提供详细的解决方案介绍和定制化服务。
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-624">
            <Link to="/about/contact" className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-8 rounded-md transition duration-300" data-id="element-625">
              联系我们
            </Link>
            <Link to="/solutions" className="inline-block bg-white text-primary-600 font-medium py-3 px-8 rounded-md border border-primary-600 transition duration-300 hover:bg-primary-50" data-id="element-626">
              返回解决方案列表
            </Link>
          </div>
        </div>
      </section>
    </div>;
};
export default SolutionDetail;